import LandingNavbar from "../../components/landing/landing-navbar";
import LandingFooter from "../../components/landing/landing-footer";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import LandingImageCarousel from "@/components/landing/landing-image-carousel";

export default function BarAndGrillPage() {
  const [isContentInView, setIsContentInView] = useState(false);
  const [isContactInView, setIsContactInView] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentObserver = new IntersectionObserver(
      ([entry]) => {
        setIsContentInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    const contactObserver = new IntersectionObserver(
      ([entry]) => {
        setIsContactInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    if (contentRef.current) {
      contentObserver.observe(contentRef.current);
    }

    if (contactRef.current) {
      contactObserver.observe(contactRef.current);
    }

    return () => {
      if (contentRef.current) {
        contentObserver.unobserve(contentRef.current);
      }
      if (contactRef.current) {
        contactObserver.unobserve(contactRef.current);
      }
    };
  }, []);

  const contactInfo = [
    {
      icon: MapPin,
      label: "ADDRESS:",
      value: "Close to KK80 ST, Kigali, Rwanda",
    },
    {
      icon: Phone,
      label: "HOTEL TELEPHONE:",
      value: "+250 791 874 212",
    },
    {
      icon: Mail,
      label: "EMAIL:",
      value: "info@ikorohouse.com",
    },
    {
      icon: MapPin,
      label: "GENERAL MANAGER",
      value: "Gaston Rurangirwa",
    },
    {
      icon: Clock,
      label: "CHECK-IN / CHECK-OUT:",
      value: "14h00 / 11h00",
    },
  ];
  return (
    <div className="min-h-screen flex flex-col">
      <LandingNavbar />
      <main className="flex flex-col">
        {/* Hero Section */}
        <LandingImageCarousel />

        {/* Main Content Section with Images */}
        <div
          ref={contentRef}
          className="w-full relative py-8 sm:py-12 md:py-16"
          style={{ backgroundColor: "#000000" }}
        >
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div 
              className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 transition-all duration-1000 ease-out"
              style={{
                opacity: isContentInView ? 1 : 0,
                transform: isContentInView 
                  ? 'perspective(1000px) translateZ(0) scale(1)' 
                  : 'perspective(1000px) translateZ(-300px) scale(0.6)'
              }}
            >
              {/* Left Side - Text Content */}
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                  Welcome to Ikoro House Grill & Bar, where tantalizing flavors
                  and captivating ambiance await.
                </h2>

                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                  Bar:
                </h3>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-white">
                  Our beef grill takes center stage, offering an
                  unforgettable dining experience. Indulge in mouth-watering
                  creations like hickory-rubbed pork belly and succulent Jack
                  Daniel's ribs. Prepared with utmost care, our dishes promise
                  home-comfort dining and special-occasion celebrations.
                </p>

                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                  Grill:
                </h3>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-white">
                  Our vibrant Bar is the perfect spot to unwind and
                  socialize. Sip on a selection of beers, wines, and
                  expertly crafted cocktails mixed right before your eyes.
                  Complement your drinks with Swahili-style snacks and join
                  us for Happy Hour and seasonal events. Join us at Ikoro
                  House Grill & Bar and immerse yourself in a world of
                  flavors and unforgettable moments in the heart of Kigali.
                </p>
              </div>

              {/* Right Side - Image Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img 
                    src="/images/webflow/bar.jpg" 
                    alt="Bar" 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img 
                    src="/images/webflow/ikoro_food.jpg" 
                    alt="Food" 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img 
                    src="/images/webflow/ikoro_food.jpg" 
                    alt="Grill" 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="aspect-square overflow-hidden rounded-lg">
                  <img 
                    src="/images/webflow/bar.jpg" 
                    alt="Drinks" 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full relative bg-white">
          {/* Contact and Food Image Section */}
          <div ref={contactRef} className="w-full py-8 sm:py-12 md:py-16">
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
                {/* Left Section - Contact Information */}
                <div className="space-y-4 sm:space-y-6">
                  {contactInfo.map((contact, index) => {
                    const IconComponent = contact.icon;
                    return (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 sm:gap-4 transition-all duration-700 ease-out"
                        style={{
                          transitionDelay: `${index * 100}ms`,
                          opacity: isContactInView ? 1 : 0,
                          transform: isContactInView 
                            ? 'perspective(1000px) translateZ(0) translateX(0) scale(1)' 
                            : 'perspective(1000px) translateZ(-200px) translateX(-50px) scale(0.7)'
                        }}
                      >
                        <IconComponent
                          className="w-5 h-5 mt-1 flex-shrink-0 text-black"
                          strokeWidth={1.5}
                        />
                        <div className="flex-1">
                          <div
                            className="font-semibold text-sm sm:text-base mb-1"
                            style={{ color: "#2C2416" }}
                          >
                            {contact.label}
                          </div>
                          <div
                            className="text-sm sm:text-base"
                            style={{ color: "#2C2416" }}
                          >
                            {contact.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Section - Food Image */}
                <div 
                  className="w-full transition-all duration-700 ease-out"
                  style={{
                    transitionDelay: '200ms',
                    opacity: isContactInView ? 1 : 0,
                    transform: isContactInView 
                      ? 'perspective(1000px) translateZ(0) scale(1)' 
                      : 'perspective(1000px) translateZ(-200px) scale(0.7)'
                  }}
                >
                  <div className="w-full h-auto min-h-[300px] sm:min-h-[400px] md:min-h-[500px]">
                    <img
                      src="/images/webflow/ikoro_food.jpg"
                      alt="Ikoro Food"
                      className="w-full h-full object-cover rounded-lg shadow-lg"
                      style={{ maxHeight: "500px", minHeight: "300px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
